/* istanbul ignore file */
/**
 * Created by rathompson on 11/4/21
 * File: AddPermissionsModal
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState, VFC } from 'react'
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader, ModalProps,
} from '@panwds/react-ui'
import {
  DATA_TEST_ADD_IDENTITY_CANCEL,
  DATA_TEST_ADD_IDENTITY_SUBMIT,
} from '../../../../common/constants/TestingDataIds'
import {
  LABEL_ADD_PERMISSIONS_TITLE,
  BUTTON_SAVE,
  BUTTON_CANCEL
} from '../../../constants/LabelConstants'
import PermissionsTable from '../../common/permissionsTable/PermissionsTable'
import { ARRAY } from '../../../../common/utils/Utils'
import _ from 'lodash'
import type { IAM_Permission } from '../../../../common/reducers/types/IAMReducer.types'
import buildPermissionsTree from '../../../utils/buildPermissionTree'

/**
 *   Custom role permission select modal
 */
const AddPermissionsModal: VFC<ModalProps & {
  closeModal: () => void,
  data: IAM_Permission[],
  onAddPermissionSave: Function
}> = ({ closeModal, isOpen, data, onAddPermissionSave }) => {

  const [selectedRowIds, setSelectedRowIds] = useState({})
  const [rowsById, setRowsById] = useState({})
  const tableRef = useRef()

  const permissions = useMemo(() => {
    return buildPermissionsTree(data)
  }, [data])

  const tableInstanceRef = useCallback((node) => {
    if (node) {
      tableRef.current = node
      if (!_.isEqual(rowsById, node.rowsById) && Object.keys(node.rowsById).length > Object.keys(rowsById).length) {
        setRowsById(node.rowsById)
      }
      if (!_.isEqual(selectedRowIds, node.state.selectedRowIds)) {
        setSelectedRowIds(node.state.selectedRowIds)
      }
    }
  }, [selectedRowIds, rowsById])

  useEffect(() => {
    const modalMask = document.getElementsByClassName('tw-bg-blue-steel-700 tw-fixed tw-inset-0 tw-z-10 tw-transition-opacity tw-duration-200 tw-opacity-80')
    if (modalMask && modalMask.length) {
      (modalMask[0] as HTMLElement).style.zIndex = '1051'
    }
    else {
      const mask = document.getElementsByClassName('tw-bg-gray-900 tw-fixed tw-inset-0 tw-z-10 tw-transition-opacity tw-duration-200 tw-opacity-75')
      if (mask && mask.length) {
        (modalMask[0] as HTMLElement).style.zIndex = '1051'
      }
    }
  }, [isOpen])

  const onSaveClick = useCallback(() => {
    (tableRef.current as any)?.setGlobalFilter('')
    setTimeout(() => {
      const ids = Object.keys(selectedRowIds)
      const rows = ids.map(id => _.get(rowsById, id)?.original)
      const permissions = rows.filter((item: IAM_Permission) => !ARRAY.hasArray(item.children))
      onAddPermissionSave(permissions)
      closeModal()
    }, 200)
  }, [selectedRowIds, onAddPermissionSave, closeModal, rowsById])

  return (
    <Modal style={{ zIndex: 1052, top: '50%', '--tw-translate-y': '-50%' } as CSSProperties} size='lg' onClose={closeModal} isOpen={isOpen}>
      <div className='header-wrapper'>
        <ModalHeader title={LABEL_ADD_PERMISSIONS_TITLE} />
      </div>
      <ModalBody>
        <PermissionsTable
          data={permissions}
          expanded={false}
          {...{ tableRef, tableInstanceRef }}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          data-testid={DATA_TEST_ADD_IDENTITY_CANCEL}
          onClick={closeModal}
          appearance="secondary"
        >{BUTTON_CANCEL}</Button>
        <Button
          data-testid={DATA_TEST_ADD_IDENTITY_SUBMIT}
          appearance='primary'
          onClick={onSaveClick}
          disabled={!ARRAY.hasArray(Object.keys(selectedRowIds))}
        >{BUTTON_SAVE}</Button>
      </ModalFooter>
    </Modal>
  )
}

AddPermissionsModal.propTypes = {}
export default AddPermissionsModal
